import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useId} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {OptionItem, SingleSelect} from "@khanacademy/wonder-blocks-dropdown";
import {LabeledField} from "@khanacademy/wonder-blocks-labeled-field";
import {sizing} from "@khanacademy/wonder-blocks-tokens";
import {getLanguages} from "@khan/i18n";
import {AppShellContext} from "@khan/shared-core/context";

import useLocalesQuery, {type Locale} from "./use-locales-query.ts";

interface Props {
    // TODO(michaelpolyak): Rename this to `userKaLocale` as that's what it is.
    userLanguage: string;
    onChange: (userKaLocale: string | "unspecified") => void;
    disabled: boolean;
    label?: React.ReactNode;
}

const MAX_HEIGHT = 320;

// Adds translated language name after local language name
const languageLabels = (locale: Locale): string => {
    const translatedLanguageName = getLanguages().get(locale.id)?.languageName;

    if (
        !translatedLanguageName ||
        locale.localName === translatedLanguageName
    ) {
        return locale.localName;
    }

    return `${locale.localName} - ${translatedLanguageName}`;
};

const LanguageSelector = ({
    userLanguage: userKaLocale,
    disabled,
    onChange,
    label = t`Primary Language`,
}: Props): React.ReactNode => {
    const {entrypoint} = React.useContext(AppShellContext);
    const languageSelectorId = useId();
    const {locales, isLoading, isError} = useLocalesQuery();

    const isKhanClassroom = entrypoint === "classroom";
    // When the language selector is shown in context of Khan Classroom,
    // only the supported KA locales may be selected as user preferred.
    let selectableLocales: Array<Locale> = [];
    // If the user selected KA locale is not supported in Classroom,
    // it will still appear in the list to indicate it's the current
    // selection, but it will appear disabled. In addition, we prompt
    // the user in the below messaging to change their selection.
    let unsupportedUserLocale: Locale | null = null;

    if (isKhanClassroom) {
        for (const locale of locales) {
            if (locale.isSupportedInKhanClassroom) {
                selectableLocales.push(locale);
            } else if (locale.id === userKaLocale) {
                unsupportedUserLocale = locale;
            }
        }
    } else {
        selectableLocales = locales;
    }

    return (
        <View style={{gap: sizing.size_120}}>
            <LabeledField
                label={label}
                description={
                    unsupportedUserLocale &&
                    t`Your currently selected language is not supported. Please select a new language.`
                }
                field={
                    <SingleSelect
                        disabled={isLoading || isError || disabled}
                        onChange={onChange}
                        placeholder={t`Language`}
                        selectedValue={userKaLocale}
                        style={styles.dropdown}
                        dropdownStyle={styles.dropdownStyle}
                        id={languageSelectorId}
                    >
                        {[
                            <OptionItem
                                key="unspecified"
                                label={t`Not specified`}
                                // NOTE: This "unspecified" is a sentinal value
                                // keyed on by the backend settings mutation,
                                // to clear the user preferred KA locale.
                                // sync-start:user-ka-locale-unspecified 1454709885 https://github.com/Khan/webapp/blob/master/services/users/mutations/set_settings.go
                                value="unspecified"
                                // sync-end:user-ka-locale-unspecified
                            />,
                        ]
                            .concat(
                                selectableLocales.map((locale, index) => (
                                    <OptionItem
                                        key={locale.id}
                                        label={languageLabels(locale)}
                                        value={locale.id}
                                        horizontalRule={
                                            unsupportedUserLocale &&
                                            index ===
                                                selectableLocales.length - 1
                                                ? "full-width"
                                                : "none"
                                        }
                                    />
                                )),
                            )
                            .concat(
                                unsupportedUserLocale
                                    ? [
                                          <OptionItem
                                              key={unsupportedUserLocale.id}
                                              label={languageLabels(
                                                  unsupportedUserLocale,
                                              )}
                                              value={unsupportedUserLocale.id}
                                              disabled={true}
                                          />,
                                      ]
                                    : [],
                            )}
                    </SingleSelect>
                }
            />
        </View>
    );
};
export default LanguageSelector;

const styles = StyleSheet.create({
    dropdown: {
        width: "100%",
        maxInlineSize: 400,
    },
    dropdownStyle: {
        maxHeight: MAX_HEIGHT,
    },
});
